import axios from 'axios';
import { BaseHttpClient } from '../BaseHttpClient';
import { injectCommonInterceptors } from '../interceptors';
import {
  BootstrapPersonasRequest,
  BootstrapPersonasResponse,
  CreatePersonaPromptRequest,
  CreatePersonaPromptResponse,
  GetPersonaPromptsResponse,
  GetPersonaResponse,
  GetPersonaVersionsResponse,
  GetPersonasResponse,
  Persona,
  PersonaAgentRun,
  PersonaVersion,
  RollbackPersonaRequest,
  TriggerPersonasRefreshRequest,
  TriggerPersonasRefreshResponse,
  UpdateWorkspacePersonaConfigRequest,
  WorkspacePersonaConfig,
} from './api.schema';
import { BASE_URL } from './consts';

const axiosPersonasInstance = axios.create({
  baseURL: BASE_URL,
});

injectCommonInterceptors(axiosPersonasInstance);

const personaPath = (userLogin: string): `/persona/${string}` =>
  `/persona/${encodeURIComponent(userLogin)}`;

export class PersonasClient extends BaseHttpClient {
  constructor() {
    super(axiosPersonasInstance);
  }

  static override instance = new PersonasClient();

  /**
   * List all personas in the workspace (metadata only — no markdown content).
   */
  static async getPersonas(): Promise<GetPersonasResponse> {
    return await PersonasClient.get<GetPersonasResponse>('/personas');
  }

  /**
   * Fetch a single persona by GitHub user_login. Returns full markdown content.
   */
  static async getPersona(userLogin: string): Promise<GetPersonaResponse> {
    return await PersonasClient.get<GetPersonaResponse>(personaPath(userLogin));
  }

  /**
   * List the prompt history for a persona (newest first; capped at 100 server-side).
   */
  static async getPersonaPrompts(
    userLogin: string,
  ): Promise<GetPersonaPromptsResponse> {
    return await PersonasClient.get<GetPersonaPromptsResponse>(
      `${personaPath(userLogin)}/prompts`,
    );
  }

  /**
   * Submit an admin prompt that drives a prompt-update agent run.
   * Server returns the prompt id + the enqueued agent run id; the caller
   * polls the run via getPersonaAgentRun until terminal.
   */
  static async createPersonaPrompt(
    userLogin: string,
    payload: CreatePersonaPromptRequest,
  ): Promise<CreatePersonaPromptResponse> {
    return await PersonasClient.post<CreatePersonaPromptResponse>(
      `${personaPath(userLogin)}/prompt`,
      payload,
    );
  }

  /**
   * Poll a persona agent run by id. Used with React Query's refetchInterval
   * to drive the run-status indicator.
   */
  static async getPersonaAgentRun(
    userLogin: string,
    runId: number,
  ): Promise<PersonaAgentRun> {
    return await PersonasClient.get<PersonaAgentRun>(
      `${personaPath(userLogin)}/runs/${runId}`,
    );
  }

  /**
   * List the version history for a persona, newest first.
   */
  static async getPersonaVersions(
    userLogin: string,
  ): Promise<GetPersonaVersionsResponse> {
    return await PersonasClient.get<GetPersonaVersionsResponse>(
      `${personaPath(userLogin)}/versions`,
    );
  }

  /**
   * Fetch a single historical version of a persona with full markdown content.
   */
  static async getPersonaVersion(
    userLogin: string,
    versionNumber: number,
  ): Promise<PersonaVersion> {
    return await PersonasClient.get<PersonaVersion>(
      `${personaPath(userLogin)}/versions/${versionNumber}`,
    );
  }

  /**
   * Roll back the persona to an older version. Creates a new version row whose
   * content mirrors the target and flips the persona's current_version_id.
   */
  static async rollbackPersona(
    userLogin: string,
    payload: RollbackPersonaRequest,
  ): Promise<Persona> {
    return await PersonasClient.post<Persona>(
      `${personaPath(userLogin)}/rollback`,
      payload,
    );
  }

  /**
   * Read the workspace's persona-refresh dispatcher configuration.
   */
  static async getPersonasConfig(): Promise<WorkspacePersonaConfig> {
    return await PersonasClient.get<WorkspacePersonaConfig>('/personas/config');
  }

  /**
   * Patch the workspace's persona-refresh dispatcher configuration.
   * Admin-gated server-side; the UI mirrors that gating via
   * `useTeamProfilesAdmin`.
   */
  static async updatePersonasConfig(
    payload: UpdateWorkspacePersonaConfigRequest,
  ): Promise<WorkspacePersonaConfig> {
    return await PersonasClient.patch<WorkspacePersonaConfig>(
      '/personas/config',
      payload,
    );
  }

  /**
   * Enqueue refresh runs for one / many / all personas in the workspace.
   * Admin-gated server-side; an empty / omitted `userLogins` refreshes every
   * persona.
   */
  static async triggerPersonasRefresh(
    payload: TriggerPersonasRefreshRequest = {},
  ): Promise<TriggerPersonasRefreshResponse> {
    return await PersonasClient.post<TriggerPersonasRefreshResponse>(
      '/personas/refresh',
      payload,
    );
  }

  /**
   * Seed personas for a brand-new workspace. When `userLogins` is omitted
   * the backend auto-discovers candidate developers from the workspace's
   * connected orgs. On success the new persona rows are created and an
   * initial generate run is enqueued for each.
   */
  static async bootstrapPersonas(
    payload: BootstrapPersonasRequest = {},
  ): Promise<BootstrapPersonasResponse> {
    return await PersonasClient.post<BootstrapPersonasResponse>(
      '/personas/bootstrap',
      payload,
    );
  }
}
